import React from "react";
import { useState, useEffect } from "react";
import { IMember } from "../../types/interfaces/IMember";
import { IMemberAddress } from "../../types/interfaces/IMemberAddress";
import { CardContent } from "@mui/material";
import { AdvancedMarker, APIProvider, Map } from '@vis.gl/react-google-maps';
import { setKey, setLanguage, setRegion, fromAddress } from "react-geocode";


/**
 * MapCard Component
 * 
 * This component displays a map based on the member's address.
 * 
 * Props:
 * - member: An object of type IMember containing the member's information.
 * 
 * Example usage:
 * ```tsx
 * <MapCard member={member} />
 * ```
 * 
 * @param {IMember} member - The member object containing the address information.
 * @returns {JSX.Element} A card displaying the map location of the member.
 */
export default function MapCard({ member }: { member: IMember }) {
    const [coordinates, setCoordinates] = useState({ lat: 0, lng: 0 });

    const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY!;
    setKey(GOOGLE_API_KEY);
    setLanguage('en');
    setRegion('us');

    useEffect(() => {
        const getCoordinates = async () => {
            const memberAddress = getAddressFromMember(member);
            if (memberAddress === null) return;

            if (memberAddress.Latitude !== 0 && memberAddress.Longitude !== 0) {
                setCoordinates({ lat: memberAddress.Latitude, lng: memberAddress.Longitude });
                return;
            }
            getGeoLocation(memberAddress);
        };
        getCoordinates();
    }, [member]);

    const getAddressFromMember = (member: IMember): IMemberAddress | null => {
        return member.Addresses !== null
            && member.Addresses !== undefined
            && member.Addresses.length > 0 ? member.Addresses[0] : null;
    };

    const getGeoLocation = (memberAddress: IMemberAddress): void => {
        let strAddress = memberAddress.AddressLine1 + ", "
            + memberAddress.City + ", "
            + memberAddress.State + " "
            + memberAddress.Country + " "
            + memberAddress.Zip;
        fetchCoordinatesFromAddress(strAddress);
    };

    const fetchCoordinatesFromAddress = (strAddress: string | null): void => {
        if (strAddress === null) return;

        fromAddress(strAddress)
            .then((response: { results: { geometry: { location: { lat: any; lng: any; }; }; }[]; }) => {
                const { lat, lng } = response.results[0].geometry.location;
                setCoordinates({ lat, lng });
            })
            .catch((error: any) => {
                console.error(error);
            });
    };

    return (<>
        {coordinates.lat !== 0 && coordinates.lng !== 0 && (
            <React.Fragment>
                <APIProvider apiKey={GOOGLE_API_KEY}>
                    <CardContent>
                        <Map
                            mapId="directory-map"
                            style={{ width: '100%', height: '25vh' }}
                            defaultCenter={coordinates}
                            defaultZoom={12}
                            gestureHandling={'auto'}
                            disableDefaultUI={true}>
                            <AdvancedMarker position={coordinates} />
                        </Map>
                    </CardContent>
                </APIProvider>
            </React.Fragment>
        )}
    </>
    );
}